import { useState, useEffect, useCallback, useRef } from 'react';
import { MarketSelector } from './MarketSelector';
import { OrderEntry } from './OrderEntry';
import { AccountEquity } from './AccountEquity';
import { TradingChart } from './TradingChart';
import { Orderbook } from './Orderbook';
import { OrderStatus } from './OrderStatus';
import { BalancesTab } from './BalancesTab';
import { PositionsTab } from './PositionsTab';
import { OpenOrdersTab } from './OpenOrdersTab';
import { TradeHistoryTab } from './TradeHistoryTab';
import { FundingHistoryTab } from './FundingHistoryTab';
import { OrderHistoryTab } from './OrderHistoryTab';
import { OrderConfirmationModal } from './OrderConfirmationModal';
import { quoteAggregator } from '../services/aggregator';
import { hyperliquidClient } from '../services/hyperliquid';
import { atlasApi } from '../services/atlasApi';
import { marketDataService, type MarketStats } from '../services/marketData';
import { marketDataWs, type MarketDataUpdate } from '../services/marketDataWebsocket';
import { useOrderExecution } from '../hooks/useOrderExecution';
import type { Hip3Market, SmartRoute } from '../types';
import { formatPrice, formatPercentage } from '../utils/formatters';

// Pending order state for confirmation modal
interface PendingOrder {
  side: 'buy' | 'sell';
  size: number;
  type: 'market' | 'limit';
  price?: number;
  leverage: number;
}

export function TradingInterfacePro() {
  const [markets, setMarkets] = useState<Hip3Market[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<Hip3Market | null>(null);
  const [smartRoute, setSmartRoute] = useState<SmartRoute | null>(null);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(true);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [marketStats, setMarketStats] = useState<Map<string, MarketStats>>(new Map());
  const [activeTab, setActiveTab] = useState<string>('Balances');
  const [showMobileOrderEntry, setShowMobileOrderEntry] = useState(false);
  const [fundingCountdown, setFundingCountdown] = useState<string>('00:00:00');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Order confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<PendingOrder | null>(null);

  const { execute, isExecuting, lastResult } = useOrderExecution();

  // Discover markets on mount
  useEffect(() => {
    const loadMarkets = async () => {
      setIsLoadingMarkets(true);
      try {
        let discoveredMarkets: Hip3Market[];

        // Try to load markets from backend first
        try {
          console.log('Loading markets from Atlas backend...');
          const backendResponse = await atlasApi.getMarkets();
          discoveredMarkets = atlasApi.convertToHip3Markets(backendResponse.markets);
          console.log(`✓ Loaded ${discoveredMarkets.length} markets from backend (cached data)`);
        } catch (backendError) {
          console.warn('Backend unavailable, falling back to direct Hyperliquid discovery:', backendError);
          discoveredMarkets = await hyperliquidClient.discoverMarkets();
          console.log(`✓ Loaded ${discoveredMarkets.length} markets directly from Hyperliquid`);
        }

        // Filter to only show assets that have at least one HIP-3 market
        const baseAssetsWithHip3 = new Set(
          discoveredMarkets
            .filter(m => !m.isNative)
            .map(m => m.baseAsset)
        );

        console.log('Assets with HIP-3 markets:', Array.from(baseAssetsWithHip3));

        // Keep markets for assets that have HIP-3 (can include native for those assets)
        const filteredMarkets = discoveredMarkets.filter(m =>
          baseAssetsWithHip3.has(m.baseAsset)
        );

        console.log(`Filtered from ${discoveredMarkets.length} to ${filteredMarkets.length} markets (HIP-3 assets only)`);

        setMarkets(filteredMarkets);
        quoteAggregator.setMarkets(filteredMarkets);

        // Fetch initial market stats (this will also try backend first)
        const stats = await marketDataService.fetchMarketStats(filteredMarkets);
        setMarketStats(stats);

        // Set default market to XYZ-100 if available
        const defaultMarket = filteredMarkets.find(
          m => m.symbol === 'xyz:XYZ100' || (m.baseAsset === 'XYZ100' && m.provider === 'xyz')
        );
        if (defaultMarket) {
          console.log('Setting default market to XYZ-100:', defaultMarket);
          setSelectedMarket(defaultMarket);
        }
      } catch (error) {
        console.error('Error loading markets:', error);
      } finally {
        setIsLoadingMarkets(false);
      }
    };

    loadMarkets();
  }, []);

  // Poll market stats
  useEffect(() => {
    if (markets.length === 0) return;

    const pollMarketStats = async () => {
      try {
        const stats = await marketDataService.fetchMarketStats(markets);
        setMarketStats(stats);
      } catch (error) {
        console.error('Error polling market stats:', error);
      }
    };

    // Initial fetch
    pollMarketStats();

    // Poll every 30 seconds for volume/OI/funding (slower since less critical)
    const interval = setInterval(pollMarketStats, 30000);
    return () => clearInterval(interval);
  }, [markets]);

  // Subscribe to realtime mark/oracle price updates for selected market
  useEffect(() => {
    if (!selectedMarket) return;

    const handleMarketDataUpdate = (update: MarketDataUpdate) => {
      // Update the market stats with realtime mark/oracle prices
      setMarketStats(prev => {
        const newStats = new Map(prev);
        const currentStats = newStats.get(selectedMarket.id);

        if (currentStats) {
          newStats.set(selectedMarket.id, {
            ...currentStats,
            lastPrice: update.markPrice,
          });
        }

        return newStats;
      });
    };

    marketDataWs.subscribe(selectedMarket.symbol, handleMarketDataUpdate, selectedMarket.dexIndex);

    return () => {
      marketDataWs.unsubscribe(handleMarketDataUpdate);
    };
  }, [selectedMarket]);

  // Update funding countdown timer every second
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0);

      const diff = nextHour.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setFundingCountdown(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Note: Quote polling removed to prevent rate limits
  // Quotes are now only fetched on-demand when user changes order size (see handleSizeChange)

  // Debounced smart route recalculation when user changes order size
  const handleSizeChange = useCallback((newSize: number, newSide: 'buy' | 'sell') => {
    if (!selectedMarket || newSize <= 0) {
      setSmartRoute(null);
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set loading state immediately
    setIsLoadingRoute(true);

    // Debounce the calculation by 500ms
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const smart = await quoteAggregator.calculateSmartRoute(
          selectedMarket.baseAsset,
          newSide,
          newSize
        );
        setSmartRoute(smart);
      } catch (error) {
        console.error('Error calculating smart route:', error);
        setSmartRoute(null);
      } finally {
        setIsLoadingRoute(false);
      }
    }, 500);
  }, [selectedMarket]);

  // Show confirmation modal instead of directly executing
  const handleSubmitOrder = (
    side: 'buy' | 'sell',
    size: number,
    type: 'market' | 'limit',
    price?: number,
    leverage?: number
  ) => {
    if (!selectedMarket) return;

    // Store pending order and show modal
    setPendingOrder({
      side,
      size,
      type,
      price,
      leverage: leverage || 1,
    });
    setShowConfirmModal(true);
  };

  // Execute order after confirmation
  const handleConfirmOrder = async () => {
    if (!selectedMarket || !pendingOrder) return;

    console.log('Executing confirmed order:', {
      market: selectedMarket.symbol,
      provider: selectedMarket.provider,
      ...pendingOrder,
    });

    const result = await execute(
      selectedMarket,
      pendingOrder.side,
      pendingOrder.size,
      pendingOrder.type,
      pendingOrder.price
    );

    console.log('Order result:', result);

    // Close modal after execution (success or fail)
    setShowConfirmModal(false);
    setPendingOrder(null);
  };

  // Cancel order
  const handleCancelOrder = () => {
    setShowConfirmModal(false);
    setPendingOrder(null);
  };

  if (isLoadingMarkets) {
    return (
      <div className="h-full bg-[#000000] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2962ff] mb-4"></div>
          <div className="text-white text-[14px] font-medium">Loading markets from Hyperliquid...</div>
          <div className="text-[#6c7884] text-[13px] mt-2">Discovering HIP-3 venues</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#1a1b1d] overflow-hidden">
      {/* Top Bar */}
      <div className="border-b border-[#2d3748] bg-[#1a1b1d] px-2 sm:px-4 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 h-[60px] overflow-x-auto">
          {/* Market Selector - Integrated */}
          <div className="flex-shrink-0">
            <MarketSelector
              markets={markets}
              selectedMarket={selectedMarket}
              onSelect={setSelectedMarket}
              marketData={marketStats}
            />
          </div>

          {/* Market Indicators */}
          {selectedMarket && marketStats.size > 0 && (
            <>
              {(() => {
                const stats = marketStats.get(selectedMarket.id);

                // Fallback: if no stats for selected market, don't show indicators
                if (!stats) {
                  return null;
                }

                // Calculate absolute change for display
                const absoluteChange = stats.lastPrice * (stats.change24h / 100);

                return (
                  <>
                    {/* Mark Price */}
                    <div className="flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">Mark</div>
                      <div className="text-white text-[12px] font-normal">{formatPrice(stats.lastPrice, selectedMarket.symbol)}</div>
                    </div>

                    {/* Oracle Price - hidden on mobile */}
                    <div className="hidden md:flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">Oracle</div>
                      <div className="text-white text-[12px] font-normal">{formatPrice(stats.lastPrice, selectedMarket.symbol)}</div>
                    </div>

                    {/* 24h Change - showing both absolute and percentage */}
                    <div className="flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">24H Change</div>
                      <div className={`font-mono text-[12px] font-normal ${stats.change24h >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                        {absoluteChange >= 0 ? '+' : ''}{absoluteChange.toFixed(2)} / {formatPercentage(stats.change24h)}
                      </div>
                    </div>

                    {/* 24h Volume */}
                    <div className="flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">24H Volume</div>
                      <div className="text-white text-[12px] font-normal">{(() => {
                        // Sum volume across all venues for this baseAsset
                        let totalVolume = 0;
                        markets.forEach(m => {
                          if (m.baseAsset === selectedMarket.baseAsset) {
                            const marketVolume = marketStats.get(m.id)?.volume24h || 0;
                            totalVolume += marketVolume;
                          }
                        });
                        return `$${totalVolume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      })()}</div>
                    </div>

                    {/* Open Interest - hidden on small mobile */}
                    <div className="hidden sm:flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">Open Interest</div>
                      <div className="text-white text-[12px] font-normal">{(() => {
                        // Sum OI across all venues for this baseAsset
                        let totalOI = 0;
                        markets.forEach(m => {
                          if (m.baseAsset === selectedMarket.baseAsset) {
                            const marketOI = marketStats.get(m.id)?.openInterest || 0;
                            totalOI += marketOI;
                          }
                        });
                        return totalOI.toLocaleString('en-US', { maximumFractionDigits: 0 });
                      })()}</div>
                    </div>

                    {/* Funding / Countdown - hidden on mobile */}
                    <div className="hidden lg:flex flex-col gap-0.5">
                      <div className="text-[11px] text-[#888] font-normal">Funding / Countdown</div>
                      <div className="text-white text-[12px] font-normal">
                        <span className={stats.funding8h >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}>
                          {formatPercentage(stats.funding8h, 4)}
                        </span>
                        <span className="text-[#888]"> {fundingCountdown}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* Right side spacer */}
          <div className="flex-1" />
        </div>
      </div>

      {/* Main Trading Interface */}
      <div className="flex-1 flex flex-col w-full px-1 sm:px-2 py-1 sm:py-2 gap-[1px] overflow-hidden">
        {/* Top Row: Chart + Orderbook + Order Entry */}
        <div className="grid grid-cols-12 gap-[1px] flex-shrink-0" style={{ minHeight: '300px' }}>
          {/* Left Side: Chart */}
          <div className="col-span-12 xl:col-span-8">
            <TradingChart
              symbol={selectedMarket?.symbol || ''}
              dexIndex={selectedMarket?.dexIndex}
              currentPrice={selectedMarket ? marketStats.get(selectedMarket.id)?.lastPrice : undefined}
              szDecimals={selectedMarket?.szDecimals}
            />
          </div>

          {/* Right Side: Orderbook + Order Entry side by side - hidden on smaller screens */}
          <div className="hidden xl:grid col-span-4 grid-cols-2 gap-[1px]">
            {/* Orderbook */}
            <div className="col-span-1">
              <Orderbook
                symbol={selectedMarket?.symbol || ''}
                dex={selectedMarket?.dexIndex}
              />
            </div>

            {/* Order Entry */}
            <div className="col-span-1">
              <OrderEntry
                selectedMarket={selectedMarket}
                onSubmitOrder={handleSubmitOrder}
                onSizeChange={handleSizeChange}
                bestRoute={null}
                smartRoute={smartRoute}
                isLoading={isExecuting}
                isLoadingRoute={isLoadingRoute}
                currentPrice={selectedMarket ? marketStats.get(selectedMarket.id)?.lastPrice : undefined}
              />
            </div>
          </div>
        </div>

        {/* Bottom Row: Tabs + Account Equity - Aligned with top grid */}
        <div className="flex-1 grid grid-cols-12 gap-[1px] min-h-0">
          {/* Tab Section - Aligns with Chart + Orderbook (10 columns) */}
          <div className="col-span-12 xl:col-span-10 flex flex-col bg-[#0e0f10] rounded-md border border-[#1a1d20] overflow-hidden min-h-0">
            {/* Tab Headers - horizontal scroll on mobile */}
            <div className="border-b border-[#1a1d20] flex items-center px-2 flex-shrink-0 overflow-x-auto scrollbar-hide">
              {['Balances', 'Positions', 'Open Orders', 'Trade History', 'Funding History', 'Order History', 'Orderbook', 'Account'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 sm:px-4 py-2.5 text-[12px] font-semibold cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    tab === activeTab
                      ? 'text-white border-b-2 border-[#2962ff] -mb-[2px]'
                      : 'text-[#6c7884] hover:text-[#b0b8c1]'
                  } ${
                    (tab === 'Orderbook' || tab === 'Account') ? 'xl:hidden' : ''
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content - Fills remaining space */}
            <div className="flex-1 overflow-auto">
              {activeTab === 'Balances' && <BalancesTab />}
              {activeTab === 'Positions' && <PositionsTab />}
              {activeTab === 'Open Orders' && <OpenOrdersTab />}
              {activeTab === 'Trade History' && <TradeHistoryTab />}
              {activeTab === 'Funding History' && <FundingHistoryTab />}
              {activeTab === 'Order History' && <OrderHistoryTab />}
              {activeTab === 'Orderbook' && (
                <div className="xl:hidden p-4">
                  <Orderbook
                    symbol={selectedMarket?.symbol || ''}
                    dex={selectedMarket?.dexIndex}
                  />
                </div>
              )}
              {activeTab === 'Account' && (
                <div className="xl:hidden p-4">
                  <AccountEquity />
                </div>
              )}
            </div>
          </div>

          {/* Account Equity - Aligns with Order Entry (2 columns) */}
          <div className="hidden xl:block col-span-2">
            <AccountEquity />
          </div>
        </div>
      </div>

      {/* Floating Action Button for Mobile Order Entry */}
      <button
        onClick={() => setShowMobileOrderEntry(true)}
        className="xl:hidden fixed bottom-6 right-6 w-14 h-14 bg-[#2962ff] hover:bg-[#4176ff] rounded-full shadow-lg flex items-center justify-center z-50 transition-all active:scale-95"
        aria-label="Open order entry"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Mobile Order Entry Modal */}
      {showMobileOrderEntry && (
        <div className="xl:hidden fixed inset-0 bg-black/60 z-50 flex items-end" onClick={() => setShowMobileOrderEntry(false)}>
          <div
            className="bg-[#0e0f10] w-full rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-1.5 bg-[#2d3748] rounded-full" />
            </div>

            <OrderEntry
              selectedMarket={selectedMarket}
              onSubmitOrder={(side, size, type, price, leverage) => {
                handleSubmitOrder(side, size, type, price, leverage);
                setShowMobileOrderEntry(false);
              }}
              onSizeChange={handleSizeChange}
              bestRoute={null}
              smartRoute={smartRoute}
              isLoading={isExecuting}
              isLoadingRoute={isLoadingRoute}
              currentPrice={selectedMarket ? marketStats.get(selectedMarket.id)?.lastPrice : undefined}
            />
          </div>
        </div>
      )}

      {/* Order Status Notification */}
      <OrderStatus result={lastResult} onDismiss={() => {}} />

      {/* Order Confirmation Modal */}
      {selectedMarket && pendingOrder && (
        <OrderConfirmationModal
          isOpen={showConfirmModal}
          onClose={handleCancelOrder}
          onConfirm={handleConfirmOrder}
          isLoading={isExecuting}
          market={selectedMarket}
          side={pendingOrder.side}
          size={pendingOrder.size}
          type={pendingOrder.type}
          price={pendingOrder.price}
          leverage={pendingOrder.leverage}
          smartRoute={smartRoute}
          currentPrice={marketStats.get(selectedMarket.id)?.lastPrice}
        />
      )}
    </div>
  );
}
